/**
 * AI-Powered Business Analytics Dashboard
 * Generates LLM-driven analysis of sales data, forecasts, and trends.
 */
import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Divider,
  Chip,
  Grid,
} from '@mui/material';
import { AutoAwesome, Refresh } from '@mui/icons-material';
import api from '../../services/api';
import { useGlobalFilters } from '../../context/FilterContext';
import { useCurrencyFormat } from '../../hooks/useCurrencyFormat';

interface DataSummary {
  total_revenue: number;
  total_quantity: number;
  transaction_count: number;
  months_analyzed: number;
  top_products_count: number;
}

interface AIInsightResult {
  analysis: string;
  scope: string;
  scope_id: string | null;
  period_months: number;
  data_summary: DataSummary;
  generated_at: string;
}

/** Lightweight markdown → HTML for rendering LLM output (no external dep). */
function renderMarkdown(md: string): string {
  let html = md
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    // headings
    .replace(/^### (.+)$/gm, '<h3>$1</h3>')
    .replace(/^## (.+)$/gm, '<h2>$1</h2>')
    .replace(/^# (.+)$/gm, '<h1>$1</h1>')
    // bold / italic
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    // unordered list items
    .replace(/^- (.+)$/gm, '<li>$1</li>')
    // paragraphs (double newlines)
    .replace(/\n\n/g, '</p><p>')
    // single newlines within a paragraph → <br>
    .replace(/\n/g, '<br/>');

  // Wrap consecutive <li> in <ul>
  html = html.replace(/((?:<li>.*?<\/li><br\/>?)+)/g, '<ul>$1</ul>');
  // Clean up stray <br/> inside <ul>
  html = html.replace(/<\/li><br\/><li>/g, '</li><li>');

  return `<p>${html}</p>`;
}

const scopeOptions = [
  { value: 'store', label: 'By Store' },
  { value: 'category', label: 'By Category' },
  { value: 'brand', label: 'By Brand' },
];

const periodOptions = [
  { value: 3, label: '3 Months' },
  { value: 6, label: '6 Months' },
  { value: 12, label: '12 Months' },
];

const AIInsightsDashboard: React.FC = () => {
  const globalFilters = useGlobalFilters();
  const { fmtCurrencyPrecise: formatCurrency, fmtCurrencyLabel } = useCurrencyFormat();
  const [scope, setScope] = useState('store');
  const [periodMonths, setPeriodMonths] = useState(6);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<AIInsightResult | null>(null);

  // Derive scope_id from global filters
  const getScopeId = (): string | undefined => {
    if (scope === 'store' && globalFilters.selectedStoreId) return globalFilters.selectedStoreId;
    if (scope === 'category' && globalFilters.selectedCategory) return globalFilters.selectedCategory;
    if (scope === 'brand' && globalFilters.selectedBrand) return globalFilters.selectedBrand;
    return undefined;
  };

  const handleGenerate = async () => {
    setLoading(true);
    setError(null);
    try {
      const scopeId = getScopeId();
      const response = await api.generateAIInsights({
        scope,
        scope_id: scopeId,
        period_months: periodMonths,
      });
      setResult(response.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to generate AI insights');
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (n: number) =>
    new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(n);

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <AutoAwesome sx={{ fontSize: 28, color: 'primary.main' }} />
        <Typography variant="h5" fontWeight={600}>
          AI Business Insights
        </Typography>
      </Box>

      {/* Controls */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Analysis Scope</InputLabel>
              <Select
                value={scope}
                label="Analysis Scope"
                onChange={(e) => setScope(e.target.value)}
              >
                {scopeOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Period</InputLabel>
              <Select
                value={periodMonths}
                label="Period"
                onChange={(e) => setPeriodMonths(Number(e.target.value))}
              >
                {periodOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Button
              variant="contained"
              fullWidth
              startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <AutoAwesome />}
              onClick={handleGenerate}
              disabled={loading}
              sx={{ height: 40 }}
            >
              {loading ? 'Generating...' : 'Generate Analysis'}
            </Button>
          </Grid>
        </Grid>

        {/* Show active filter context */}
        {getScopeId() && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary">
              Scope filter from global bar:
            </Typography>
            <Chip
              label={`${scope}: ${getScopeId()}`}
              size="small"
              sx={{ ml: 1 }}
              color="primary"
              variant="outlined"
            />
          </Box>
        )}
      </Paper>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Results */}
      {result && (
        <>
          {/* Summary Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {[
              { label: 'Total Revenue', value: formatCurrency(result.data_summary.total_revenue) },
              { label: 'Units Sold', value: formatNumber(result.data_summary.total_quantity) },
              { label: 'Transactions', value: formatNumber(result.data_summary.transaction_count) },
              { label: 'Months Analyzed', value: result.data_summary.months_analyzed },
              { label: 'Top Products', value: result.data_summary.top_products_count },
            ].map((card) => (
              <Grid item xs={6} sm={4} md key={card.label}>
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary">
                    {card.label}
                  </Typography>
                  <Typography variant="h6" fontWeight={600}>
                    {card.value}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          {/* Analysis Content */}
          <Paper sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" fontWeight={600}>
                Analysis Report
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Generated: {new Date(result.generated_at).toLocaleString()}
                </Typography>
                <Button
                  size="small"
                  startIcon={<Refresh />}
                  onClick={handleGenerate}
                  disabled={loading}
                >
                  Refresh
                </Button>
              </Box>
            </Box>
            <Divider sx={{ mb: 3 }} />
            <Box
              sx={{
                '& h2': { fontSize: '1.25rem', fontWeight: 600, mt: 3, mb: 1 },
                '& h3': { fontSize: '1.1rem', fontWeight: 600, mt: 2, mb: 1 },
                '& ul': { pl: 3 },
                '& li': { mb: 0.5 },
                '& p': { mb: 1.5, lineHeight: 1.7 },
                '& strong': { color: 'primary.main' },
              }}
            >
              <div dangerouslySetInnerHTML={{ __html: renderMarkdown(result.analysis) }} />
            </Box>
          </Paper>
        </>
      )}

      {/* Empty state */}
      {!result && !loading && !error && (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <AutoAwesome sx={{ fontSize: 48, color: 'action.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            AI-Powered Business Analysis
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 500, mx: 'auto' }}>
            Select an analysis scope and period, then click "Generate Analysis" to get
            an AI-generated business review of your sales data, trends, and recommendations.
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default AIInsightsDashboard;
